import { Dispatch } from '@reduxjs/toolkit';
import { ApiConstants } from '../constants/ApiConstants';
import axiosInstance from '../initializers/axios';
import IPermission from '../interfaces/IPermission';
import IUser from '../interfaces/IUser';
import IUserMenu from '../interfaces/IUserMenu';
import { SetEnums } from '../store/stores/allEnums/allEnums.store';
import { setMenu } from '../store/stores/user/menu.store';
import { setPermission } from '../store/stores/user/permission.store';
import { setUser } from '../store/stores/user/user.store';

let enumsRequest: Promise<void> | null = null;
let userAccessRequest: Promise<void> | null = null;
let lastEnumsUserKey: string | null = null;
let lastUserAccessKey: string | null = null;

const getUserKey = (currentUser: IUser | null) => {
  if (!currentUser) {
    return null;
  }

  return `${currentUser.id ?? '0'}:${currentUser.userName ?? ''}`;
};

const hasEnumValues = (enums: Record<string, any> | null | undefined) => {
  if (!enums || typeof enums !== 'object') {
    return false;
  }

  return Object.keys(enums).some((key) => key !== 'password');
};

const hasItems = <T>(items: T[] | null | undefined) => Array.isArray(items) && items.length > 0;

export const ensureEnumsLoaded = async (dispatch: Dispatch<any>, currentUser: IUser | null, enums: Record<string, any> | null | undefined) => {
  const userKey = getUserKey(currentUser);
  if (!userKey) {
    lastEnumsUserKey = null;
    return;
  }

  if (hasEnumValues(enums)) {
    lastEnumsUserKey = null;
    return;
  }

  if (lastEnumsUserKey === userKey) {
    return;
  }

  if (!enumsRequest) {
    lastEnumsUserKey = userKey;
    enumsRequest = axiosInstance
      .get(ApiConstants.enum.allEnums)
      .then((response) => {
        if (hasEnumValues(response?.data)) {
          dispatch(SetEnums(response.data));
        }
      })
      .catch((error) => {
        console.error('Failed to lazy-load enums:', error);
      })
      .finally(() => {
        enumsRequest = null;
      });
  }

  await enumsRequest;
};

export const ensureUserAccessLoaded = async (
  dispatch: Dispatch<any>,
  currentUser: IUser | null,
  menu: IUserMenu[] | null | undefined,
  permissions: IPermission[] | null | undefined,
) => {
  const userKey = getUserKey(currentUser);
  if (!userKey) {
    lastUserAccessKey = null;
    return;
  }

  if (hasItems(menu) && hasItems(permissions)) {
    lastUserAccessKey = null;
    return;
  }

  if (lastUserAccessKey === userKey) {
    return;
  }

  if (!userAccessRequest) {
    lastUserAccessKey = userKey;
    userAccessRequest = axiosInstance
      .get('/api/system/user/info')
      .then((response) => {
        const data = response?.data;
        dispatch(setPermission(Array.isArray(data?.permissions) ? data.permissions : []));
        dispatch(setMenu(Array.isArray(data?.menu) ? data.menu : []));

        if (data?.user) {
          dispatch(setUser(data.user));
        }
      })
      .catch((error) => {
        console.error('Failed to lazy-load menu/permissions:', error);
      })
      .finally(() => {
        userAccessRequest = null;
      });
  }

  await userAccessRequest;
};
